<script setup lang="ts">
import {useForm} from 'vee-validate'
import {z} from 'zod'
import {toTypedSchema} from '@vee-validate/zod'
import {motion} from 'motion-v'

const props = defineProps<{
  title?: string
  description?: string
}>()

const {newsletter, subscribe} = useNewsletter()
const state = ref<'show-box' | 'show-success' | 'hidden'>(newsletter.value ? 'hidden' : 'show-box')

const formSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
})

type Form = z.infer<typeof formSchema>

const {handleSubmit, isSubmitting} = useForm({
  validationSchema: toTypedSchema(formSchema),
})

const onSubmit = handleSubmit(async (values: Form) => {
  state.value = 'show-success'
  await subscribe(values.email)
})
</script>

<template>
  <LayoutGroup>
    <motion.div v-if="state !== 'hidden'" layout class="not-prose relative my-8 rounded-xl p-6 bordershadow-lg-gray">
      <AnimatePresence mode="popLayout">
        <motion.div
          v-if="state === 'show-box'"
          class="flex flex-col"
          :initial="{opacity: 0}"
          :animate="{opacity: 1}"
          :exit="{opacity: 0}"
          :transition="{duration: 0.3}"
        >
          <h3 class="font-semibold mb-2 text-lg text-gray-900">{{ title }}</h3>
          <p class="mb-4 text-sm text-gray-600">{{ description }}</p>

          <form class="items-top flex flex-col gap-3 xs:flex-row" novalidate @submit="onSubmit">
            <InputField
              name="email"
              placeholder="Enter your email"
              aria-label="Your email"
              type="email"
              class="flex-1"
              required
            />
            <Action type="submit" :disabled="isSubmitting" intent="main" class="h-10">
              <span v-if="isSubmitting">
                <Icon name="lucide:loader-2" class="animate-spin mr-2 inline h-4 w-4" />
                Subscribing...
              </span>
              <span v-else>Subscribe</span>
            </Action>
          </form>
        </motion.div>

        <motion.div
          v-else
          class="flex flex-col items-center justify-center text-center"
          :initial="{opacity: 0, scale: 0.95}"
          :animate="{opacity: 1, scale: 1}"
          :transition="{duration: 0.4, ease: 'easeOut'}"
        >
          <div class="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <Icon name="lucide:check" class="h-6 w-6 text-green-600" />
          </div>
          <h3 class="font-semibold mb-2 text-lg text-gray-900">Thanks for subscribing!</h3>
          <p class="text-sm text-gray-600">You'll receive our latest articles and updates in your inbox.</p>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  </LayoutGroup>
</template>
